import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { 
  GraduationCap, 
  VideoCamera, 
  Certificate, 
  ChartLineUp,
  CheckCircle,
  ArrowRight,
  BookOpen
} from '@phosphor-icons/react';

export function Landing() {
  const features = [
    {
      icon: VideoCamera,
      title: 'Videoaulas',
      description: 'Conteúdo em vídeo de alta qualidade com acompanhamento de progresso'
    },
    {
      icon: BookOpen,
      title: 'Trilhas de Aprendizagem',
      description: 'Cursos estruturados com progressão sequencial'
    },
    {
      icon: Certificate,
      title: 'Certificações',
      description: 'Ganhe certificados ao concluir os cursos'
    },
    {
      icon: ChartLineUp,
      title: 'Acompanhamento',
      description: 'Monitore sua jornada de aprendizado em tempo real'
    }
  ];

  const benefits = [
    'Acesso a treinamentos completos de produtos',
    'Aprendizado no seu ritmo, em qualquer dispositivo',
    'Quizzes e avaliações interativas',
    'Certificados com verificação por QR Code',
    'Tutoriais em vídeo com especialistas',
    'Acompanhe seu progresso em todos os cursos'
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/12306417/pexels-photo-12306417.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-0" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32 lg:py-40">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#27AE60] mb-4">
                Portal de Treinamento Profissional
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A252F] leading-none mb-6">
                Domine Suas Habilidades com o
                <span className="text-[#2C3E50]"> Aprenda Tecnoservice</span>
              </h1>
              <p className="text-lg text-[#5C6B7A] leading-relaxed mb-8 max-w-xl">
                Plataforma completa de treinamento para produtos e serviços Tecnoservice. 
                Aprenda no seu ritmo, conquiste certificações e avance na sua carreira.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/courses">
                  <Button 
                    className="bg-[#2C3E50] text-white hover:bg-[#1A252F] px-8 py-6 text-lg font-medium"
                    data-testid="hero-browse-courses"
                  >
                    Ver Cursos
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    variant="outline"
                    className="border-[#BDC3C7] text-[#2C3E50] hover:bg-[#F0F2F5] hover:border-[#2C3E50] px-8 py-6 text-lg font-medium"
                    data-testid="hero-get-started"
                  >
                    Começar Grátis
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden lg:block animate-slide-up">
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-[#2C3E50]/10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#27AE60] flex items-center justify-center">
                      <GraduationCap size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A252F]">Comece a Aprender Hoje</p>
                      <p className="text-sm text-[#5C6B7A]">Plataforma de treinamento</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} weight="fill" className="text-[#27AE60]" />
                      <span className="text-[#5C6B7A]">Cadastro gratuito</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} weight="fill" className="text-[#27AE60]" />
                      <span className="text-[#5C6B7A]">Acesso ao catálogo de cursos</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} weight="fill" className="text-[#27AE60]" />
                      <span className="text-[#5C6B7A]">Ganhe certificados</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2C3E50] mb-4">
              Recursos da Plataforma
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A252F]">
              Tudo o Que Você Precisa Para Aprender
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-[#F8F9FA] rounded-xl p-6 border border-[#2C3E50]/10 hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-[#2C3E50] flex items-center justify-center mb-4">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A252F] mb-2">{feature.title}</h3>
                <p className="text-[#5C6B7A]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjB0cmFpbmluZ3xlbnwwfHx8fDE3NzUzNTE1OTd8MA&ixlib=rb-4.1.0&q=85"
                alt="Sessão de treinamento"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#27AE60] mb-4">
                Sobre o Programa de Treinamento
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A252F] mb-6">
                Capacitando Profissionais com Conhecimento
              </h2>
              <p className="text-[#5C6B7A] leading-relaxed mb-8">
                O Aprenda Tecnoservice foi desenvolvido para fornecer treinamento completo sobre todos os nossos produtos 
                e serviços. Seja você um novo cliente ou um usuário experiente, nossos cursos irão ajudá-lo 
                a maximizar o valor das suas soluções Tecnoservice.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.slice(0, 4).map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} weight="fill" className="text-[#27AE60] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5C6B7A] text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A252F] mb-6">
            Pronto Para Começar a Aprender?
          </h2>
          <p className="text-[#5C6B7A] mb-8">
            Junte-se ao Aprenda Tecnoservice hoje e leve suas habilidades ao próximo nível.
          </p>
          <Link to="/register">
            <Button 
              className="bg-[#27AE60] text-white hover:bg-[#219653] px-8 py-6 text-lg font-medium"
              data-testid="cta-register"
            >
              Criar Conta Gratuita
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A252F] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_d24fe1d9-9454-4179-b0d7-5add74198f43/artifacts/gh9ouww7_Ativo%201.png" 
                alt="Tecnoservice" 
                className="h-10 object-contain"
              />
            </div>
            <p className="text-[#5C6B7A] text-sm">
              &copy; {new Date().getFullYear()} Tecnoservice. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
